// Polyfills for old WebViews must run before any app/SDK code.
import "./lib/polyfills";
// Sentry initialization should be imported first!
import "./instrument";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { initAppHeight } from "./lib/appHeight";
import "./index.css";
import "./lib/i18n/i18n";
import App from "./App.tsx";

// Set --app-height (concrete px viewport height) before render so the layout
// height chain stays bounded even on WebViews where 100vh/100dvh don't apply.
initAppHeight();

// =============================================================================
// CHUNK LOAD ERROR RECOVERY
// =============================================================================
// When the app resumes after a new deployment, old chunk files may no longer
// exist. The server returns index.html (404 fallback), causing:
// "TypeError: 'text/html' is not a valid JavaScript MIME type"
// This handler detects such errors and triggers a graceful reload.
// =============================================================================

const CHUNK_RELOAD_KEY = "chunk_reload_attempted";

// Check if we just reloaded due to chunk error - prevent infinite loop
const hasJustReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === "true";
if (hasJustReloaded) {
	sessionStorage.removeItem(CHUNK_RELOAD_KEY);
	console.log("📦 App reloaded after chunk error recovery");
}

// Global error handler for chunk loading failures
window.addEventListener("error", (event) => {
	const errorMessage = event.message || "";
	const isChunkError =
		errorMessage.includes("MIME type") ||
		errorMessage.includes("Failed to fetch dynamically imported module") ||
		errorMessage.includes("Loading chunk") ||
		errorMessage.includes("Loading CSS chunk") ||
		errorMessage.includes("Unexpected token '<'");

	if (isChunkError && !hasJustReloaded) {
		console.error("🔄 Stale chunk detected, reloading app...", errorMessage);

		// Clear caches if available
		if ("caches" in window) {
			caches
				.keys()
				.then((names) => Promise.all(names.map((name) => caches.delete(name))))
				.catch(() => {});
		}

		// Mark that we're reloading to prevent infinite loop
		sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");

		// Reload the page to get fresh chunks
		window.location.reload();
	}
});

// Handle unhandled promise rejections (dynamic imports fail as rejected promises)
window.addEventListener("unhandledrejection", (event) => {
	const reason = event.reason?.message || String(event.reason) || "";
	const isChunkError =
		reason.includes("MIME type") ||
		reason.includes("Failed to fetch dynamically imported module") ||
		reason.includes("Loading chunk") ||
		reason.includes("Unexpected token '<'");

	if (isChunkError && !hasJustReloaded) {
		console.error(
			"🔄 Stale chunk detected (promise rejection), reloading app...",
			reason,
		);
		sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
		window.location.reload();
	}
});

const container = document.getElementById("root");
if (!container) {
	throw new Error("Root element not found");
}
const root = createRoot(container, {
	// Callback called when an error is thrown and not caught by an ErrorBoundary.
	onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
		console.warn("Uncaught error", error, errorInfo.componentStack);
	}),
	// Callback called when React catches an error in an ErrorBoundary.
	onCaughtError: Sentry.reactErrorHandler(),
	// Callback called when React automatically recovers from errors.
	onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(<App />);
